import { useEffect, useRef, useState } from 'react'
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import { apiUrl, getAuthHeaders } from '../api/client'
import { config } from '../config'

export type ConnectionState = 'connecting' | 'connected' | 'disconnected'

export interface DataChangedEvent {
  entityType: string
  entityId: number
  action: string
  projectId?: number | null
  at: string
}

interface RealtimeOptions {
  /** Kaldes når serveren melder at data er ændret. */
  onDataChanged?: (event: DataChangedEvent) => void
  /** Kaldes ved nye aktivitetslinjer. */
  onActivity?: (activity: unknown) => void
  /** Kaldes når en TV-skærm skifter online/offline eller kvitterer for en kommando. */
  onScreenStatus?: (payload: unknown) => void
  /** Kaldes når serveren sender en kommando til denne TV-enhed. */
  onCommand?: (command: unknown) => void
  /** Device token - kun sat på TV-klienten. Brugere autentificeres med Entra-token. */
  deviceToken?: string
  enabled?: boolean
}

/**
 * Holder en SignalR-forbindelse i live og videresender serverens beskeder.
 *
 * Serveren sender bevidst kun en let "data er ændret"-besked; klienten henter
 * selv friske data bagefter. Det holder hub-beskederne små og gør at alle
 * klienter altid ser præcis det samme som REST-API-et leverer.
 */
export function useRealtime(options: RealtimeOptions) {
  const { enabled = true, deviceToken } = options
  const [state, setState] = useState<ConnectionState>('disconnected')

  // Callbacks holdes i en ref, så en ny render ikke river forbindelsen ned.
  const handlers = useRef(options)
  handlers.current = options

  useEffect(() => {
    if (!enabled) {
      return
    }

    let disposed = false
    let connection: HubConnection | null = null

    async function connect() {
      // Dev-login uden Entra sendes som query parameter, da WebSockets ikke kan bære custom headers.
      const url = deviceToken
        ? `${apiUrl('/hubs/dashboard')}?device_token=${encodeURIComponent(deviceToken)}`
        : config.devUser
          ? `${apiUrl('/hubs/dashboard')}?dev_user=${encodeURIComponent(config.devUser)}`
          : apiUrl('/hubs/dashboard')

      connection = new HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: deviceToken
            ? undefined
            : async () => {
                const headers = await getAuthHeaders()
                return headers.Authorization?.replace('Bearer ', '') ?? ''
              }
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
        .configureLogging(LogLevel.Warning)
        .build()

      connection.on('dataChanged', (event: DataChangedEvent) => handlers.current.onDataChanged?.(event))
      connection.on('activity', (activity: unknown) => handlers.current.onActivity?.(activity))
      connection.on('screenStatusChanged', (payload: unknown) => handlers.current.onScreenStatus?.(payload))
      connection.on('command', (command: unknown) => handlers.current.onCommand?.(command))

      connection.onreconnecting(() => setState('connecting'))
      connection.onreconnected(() => setState('connected'))
      connection.onclose(() => setState('disconnected'))

      try {
        setState('connecting')
        await connection.start()
        if (!disposed) setState('connected')
      } catch {
        if (!disposed) {
          setState('disconnected')
          // Prøv igen - typisk fordi serveren endnu ikke er nået op.
          setTimeout(() => {
            if (!disposed) void connect()
          }, 5000)
        }
      }
    }

    void connect()

    return () => {
      disposed = true
      if (connection && connection.state !== HubConnectionState.Disconnected) {
        void connection.stop()
      }
    }
  }, [enabled, deviceToken])

  return state
}
